<template>
  <div class="kb-page">
    <!-- ==================== 导航栏 ==================== -->
    <div class="nav-bar">
      <div class="nav-inner">
        <div class="nav-left">
          <div class="nav-logo">
            <img src="@/assets/logo.png" alt="logo" />
          </div>
          <span class="nav-title">{{ $t('nav.title') }}</span>
        </div>
        <div class="nav-right">
          <!-- 语言选择（原主题/模型位置）：点击弹出中文/English -->
          <button
            class="nav-icon-btn locale-btn"
            type="button"
            :aria-label="$t('nav.locale')"
            @click="showLocaleSheet = true"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span class="locale-label">{{ $t('nav.' + locale) }}</span>
          </button>
          <!-- ⋮ 更多（主题切换 / AI 模型） -->
          <button
            class="nav-icon-btn more-btn"
            type="button"
            :aria-label="$t('nav.more')"
            @click="showMoreSheet = true"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <circle cx="5" cy="12" r="1.8" />
              <circle cx="12" cy="12" r="1.8" />
              <circle cx="19" cy="12" r="1.8" />
            </svg>
          </button>
          <button
            v-if="!isNewSession"
            class="new-session-btn"
            @click="startNewSession"
          >{{ $t('nav.newSession') }}</button>
        </div>
      </div>
    </div>

    <!-- ==================== AI问答面板（默认显示） ==================== -->
    <div class="main-panel">
      <div class="chat-messages" ref="qaMsgRef">
        <!-- 空状态 + 常见问题 -->
        <div v-if="qaMessages.length === 0" class="welcome-area">
          <div class="welcome-icon">
            <svg viewBox="0 0 64 64" width="56" height="56">
              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#3866ee;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#1b3fa8;stop-opacity:1" />
                </linearGradient>
              </defs>
              <circle cx="32" cy="32" r="30" fill="url(#grad)" opacity="0.12" />
              <path d="M20 24 Q32 18 44 24 L44 40 Q32 46 20 40 Z" fill="url(#grad)" opacity="0.7" />
              <circle cx="28" cy="32" r="3" fill="url(#grad)" />
              <circle cx="36" cy="32" r="3" fill="url(#grad)" />
              <path d="M26 38 Q32 42 38 38" stroke="url(#grad)" stroke-width="2" fill="none" stroke-linecap="round" />
            </svg>
          </div>
          <p class="welcome-title">{{ $t('welcome.title') }}</p>
          <p class="welcome-desc">{{ $t('welcome.desc') }}</p>

          <!-- 首次引导输入区：空状态时置于「常见问题」上方，更显眼 -->
          <div class="hero-input">
            <div class="input-bar input-bar--hero">
              <div class="input-inner">
                <div class="input-row input-row--hero">
                  <div class="hero-field">
                    <input
                      v-model="qaInput"
                      class="input-field"
                      :placeholder="$t('input.placeholder')"
                      :disabled="qaLoading"
                      @keyup.enter="sendQA"
                      @focus="inputFocused = true"
                      @blur="inputFocused = false"
                    />
                  </div>
                  <button
                    class="send-btn send-btn--hero"
                    :class="{ 'send-btn--stop': streaming }"
                    :disabled="!streaming && (qaLoading || !qaInput.trim())"
                    @click="streaming ? stopGeneration() : sendQA()"
                  >
                    <svg v-if="!streaming && !qaLoading" viewBox="0 0 24 24" width="20" height="20" fill="none">
                      <path d="M22 2L11 13" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <van-loading v-else-if="!streaming" size="16" color="#fff" />
                    <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="#fff" :title="$t('toast.stopGenerate')">
                      <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="ai-disclaimer" role="note" :aria-label="$t('disclaimer')">
              <svg class="disclaimer-icon" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
              <span>{{ $t('disclaimer') }}</span>
            </div>
          </div>

          <!-- 常见问题模块 -->
          <div v-if="showFaq && !faqLoading" class="faq-section">
            <div class="faq-header">
              <span class="faq-header-title">{{ $t('faq.title') }}</span>
              <span class="faq-header-desc">{{ $t('faq.desc') }}</span>
            </div>
            <div class="faq-grid">
              <div
                v-for="(item, idx) in faqList"
                :key="idx"
                class="faq-card"
                tabindex="0"
                :style="{ animationDelay: idx * 0.05 + 's' }"
                @click="sendFAQQuestion(item.question)"
              >
                <span class="faq-category" v-if="item.category">{{ item.category }}</span>
                <span class="faq-question">{{ item.question }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 消息列表 -->
        <div v-for="(msg, idx) in qaMessages" :key="'q' + idx" :class="['msg-row', msg.role]">
          <!-- AI 头像 -->
          <div v-if="msg.role === 'ai'" class="msg-avatar">
            <svg viewBox="0 0 32 32" width="32" height="32">
              <defs>
                <linearGradient id="av-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#3866ee;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#1b3fa8;stop-opacity:1" />
                </linearGradient>
              </defs>
              <circle cx="16" cy="16" r="16" fill="url(#av-grad)" />
              <path d="M10 12 Q16 9 22 12 L22 20 Q16 23 10 20 Z" fill="#ffffff" opacity="0.9" />
              <circle cx="14" cy="16" r="1.5" fill="#2453d6" />
              <circle cx="18" cy="16" r="1.5" fill="#2453d6" />
            </svg>
          </div>
          <div class="msg-bubble">
            <!-- 用户消息保持纯文本；AI 消息按状态三态渲染：流式纯文本+光标 / 完成 Markdown / 中断纯文本 -->
            <div v-if="msg.role === 'user'" class="msg-text">{{ msg.content }}</div>
            <div v-else-if="msg.streaming" class="msg-text ai-streaming">
              <template v-if="msg.content">{{ msg.content }}<span class="stream-cursor"></span></template>
              <span v-else class="loading-dots">
                <i class="dot"></i>
                <i class="dot"></i>
                <i class="dot"></i>
              </span>
            </div>
            <div
              v-else-if="msg.completed"
              class="msg-text ai-md"
              v-html="renderMarkdown(msg.content, msg.sources)"
              @click="onMsgTextClick($event, msg)"
            ></div>
            <div v-else class="msg-text ai-streaming">{{ msg.content }}</div>
            <!-- 内容来源 -->
            <div v-if="msg.role === 'ai' && msg.sources && msg.sources.length" class="source-wrap">
              <div class="source-title-row">
                <van-icon name="notes-o" size="12" />
                {{ $t('source.title') }}
              </div>
              <div
                v-for="(src, si) in msg.sources"
                :key="si"
                class="source-item"
              >
                <van-icon name="description" size="12" color="#2453d6" />
                <span
                  class="source-name van-ellipsis"
                  :class="{ 'source-clickable': src.downloadUrl || src.url }"
                  :title="src.downloadUrl ? $t('source.preview') : (src.url ? $t('ref.openSource') : '')"
                  @click="src.downloadUrl ? viewFile(src) : src.url && openUrl(src.url)"
                >{{ src.title }}</span>
                <span v-if="src.fileType" class="source-type-badge">{{ getFileTypeLabel(src.fileType) }}</span>
                <van-icon
                  v-if="src.downloadUrl"
                  name="link-o"
                  size="12"
                  color="#2453d6"
                  class="source-copy-btn"
                  :title="$t('source.copyLink')"
                  @click.stop="copyDownloadLink(src)"
                />
                <van-icon
                  v-else-if="src.url"
                  name="link-o"
                  size="12"
                  color="#2453d6"
                  @click.stop="openUrl(src.url)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 加载中（流式模式已有占位消息展示 dots，此气泡仅用于聚合降级模式等待期） -->
        <div v-if="qaLoading && !activeStreamMsg" class="msg-row ai">
          <div class="msg-avatar">
            <svg viewBox="0 0 32 32" width="32" height="32">
              <circle cx="16" cy="16" r="16" fill="#2453d6" />
              <path d="M10 12 Q16 9 22 12 L22 20 Q16 23 10 20 Z" fill="#ffffff" opacity="0.9" />
              <circle cx="14" cy="16" r="1.5" fill="#2453d6" />
              <circle cx="18" cy="16" r="1.5" fill="#2453d6" />
            </svg>
          </div>
          <div class="msg-bubble loading-bubble">
            <span class="loading-dots">
              <i class="dot"></i>
              <i class="dot"></i>
              <i class="dot"></i>
            </span>
          </div>
        </div>
      </div>

      <!-- 输入栏（有对话后固定在底部） -->
      <div class="input-bar" v-if="qaMessages.length > 0">
        <div class="input-inner">
          <div class="input-row">
            <div class="input-field-wrap">
              <van-icon name="edit" class="input-leading-icon" />
              <input
                v-model="qaInput"
                class="input-field"
                :placeholder="$t('input.placeholder')"
                :disabled="qaLoading"
                @keyup.enter="sendQA"
                @focus="inputFocused = true"
                @blur="inputFocused = false"
              />
            </div>
            <button
              class="send-btn"
              :class="{ 'send-btn--stop': streaming }"
              :disabled="!streaming && (qaLoading || !qaInput.trim())"
              @click="streaming ? stopGeneration() : sendQA()"
            >
              <svg v-if="!streaming && !qaLoading" viewBox="0 0 24 24" width="20" height="20" fill="none">
                <path d="M22 2L11 13" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <van-loading v-else-if="!streaming" size="16" color="#fff" />
              <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="#fff" :title="$t('toast.stopGenerate')">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          </div>
          <div class="ai-disclaimer" role="note" :aria-label="$t('disclaimer')">
            <svg class="disclaimer-icon" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>{{ $t('disclaimer') }}</span>
          </div>
          <!-- 对话后：常见问题迷你条（横向滚动 + 左右箭头，点击直接提问） -->
          <div v-if="!faqLoading && faqList.length" class="faq-mini">
            <button
              v-show="faqArrowLeft"
              type="button"
              class="faq-mini-arrow faq-mini-arrow--left"
              aria-label="向左滚动"
              @click="scrollFaqMini('left')"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div
              ref="faqMiniScroll"
              class="faq-mini-scroll"
              @scroll="updateFaqArrows"
            >
              <button
                v-for="(item, idx) in faqList"
                :key="'faq-mini-' + idx"
                type="button"
                class="faq-mini-chip"
                @click="sendFAQQuestion(item.question)"
              >{{ item.question }}</button>
            </div>
            <button
              v-show="faqArrowRight"
              type="button"
              class="faq-mini-arrow faq-mini-arrow--right"
              aria-label="向右滚动"
              @click="scrollFaqMini('right')"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 文件查看器（保持不变） ==================== -->
    <van-overlay :show="showFileViewer" @click="closeFileViewer">
      <div class="file-viewer" @click.stop>
        <div class="viewer-header">
          <van-icon name="arrow-left" size="20" color="#fff" @click="closeFileViewer" />
          <span class="viewer-title van-ellipsis">{{ currentFileTitle }}</span>
          <span style="width:20px"></span>
        </div>

        <div class="viewer-body">
          <template v-if="currentFileUrl">
            <div v-if="currentFileType === 'video'" class="video-wrap">
              <video
                v-show="!videoError"
                :src="currentFileUrl"
                controls
                playsinline
                webkit-playsinline
                class="full-media"
                @error="onVideoError"
              ></video>
              <div v-if="videoError" class="video-error-box">
                <van-icon name="warning-o" size="48" color="#ff6b6b" />
                <p class="no-support-title">{{ $t('viewer.videoLoadFailed') }}</p>
                <p class="no-support-desc">{{ $t('viewer.videoErrorDesc') }}</p>
                <button class="dl-btn" @click="copyDownloadLink(currentFile)">{{ $t('viewer.copyVideoLink') }}</button>
                <button class="dl-btn" style="margin-top:10px" @click="downloadFile">{{ $t('viewer.download') }}</button>
              </div>
            </div>

            <div v-else-if="currentFileType === 'audio'" class="audio-box">
              <van-icon name="music-o" size="48" color="#2453d6" />
              <p class="audio-name">{{ currentFileTitle }}</p>
              <audio :src="currentFileUrl" controls class="audio-ctrl"></audio>
            </div>

            <van-image
              v-else-if="currentFileType === 'image'"
              :src="currentFileUrl"
              fit="contain"
              style="width:100%;height:100%"
            />

            <PdfViewer
              v-else-if="currentFileType === 'pdf'"
              :url="currentFileUrl"
              :key="currentFileUrl"
            />
          </template>

          <div v-else class="no-support">
            <van-icon :name="fileIconFallback" size="48" color="#969799" />
            <p class="no-support-title">{{ currentFileTitle }}</p>
            <p class="no-support-desc">{{ $t('viewer.notSupported') }}</p>
            <button v-if="currentFileUrl" class="dl-btn" @click="downloadFile">{{ $t('viewer.download') }}</button>
          </div>
        </div>
      </div>
    </van-overlay>

    <!-- ==================== 引用片段弹层 ==================== -->
    <van-popup
      v-model="showRefPopup"
      position="bottom"
      round
      class="ref-popup"
      :style="{ maxHeight: '70vh' }"
    >
      <div class="ref-popup-body">
        <div class="ref-popup-header">
          <span class="ref-popup-title">{{ $t('ref.title') }}</span>
          <van-icon name="cross" class="ref-popup-close" size="18" @click="showRefPopup = false" />
        </div>
        <div class="ref-popup-content">
          <p v-if="refPopupChunk && refPopupChunk.title" class="ref-chunk-title">
            {{ refPopupChunk.title }}
          </p>
          <p v-if="refPopupChunk && refPopupChunk.content" class="ref-chunk-text">
            {{ refPopupChunk.content }}
          </p>
          <p v-if="refPopupSource && !refPopupChunk" class="ref-chunk-text">
            {{ $t('ref.chunkUnavailable') }}
          </p>
          <p v-if="!refPopupSource && !refPopupChunk" class="ref-chunk-text">
            {{ $t('ref.notFound') }}
          </p>
        </div>
        <div class="ref-popup-footer">
          <button
            v-if="refPopupSource && refPopupSource.downloadUrl"
            class="ref-btn"
            @click="viewRefSource"
          >
            <van-icon name="eye-o" size="14" /> {{ $t('ref.viewDoc') }}
          </button>
          <button
            v-else-if="refPopupSource && refPopupSource.url"
            class="ref-btn"
            @click="viewRefSource"
          >
            <van-icon name="link-o" size="14" /> {{ $t('ref.openSource') }}
          </button>
        </div>
      </div>
    </van-popup>

    <!-- ==================== 语言选择 ==================== -->
    <van-action-sheet
      v-model="showLocaleSheet"
      :actions="localeActions"
      :cancel-text="$t('nav.cancel')"
      :description="$t('nav.locale')"
      @select="onLocaleSelect"
    />

    <!-- ==================== ⋮ 更多（主题切换 / AI 模型） ==================== -->
    <van-action-sheet
      v-model="showMoreSheet"
      :actions="moreActions"
      :cancel-text="$t('nav.cancel')"
      :description="$t('nav.more')"
      @select="onMoreSelect"
    />

    <!-- ==================== 模型选择 ==================== -->
    <van-action-sheet
      v-model="showModelSheet"
      :actions="modelActions"
      :cancel-text="$t('nav.cancel')"
      :description="$t('model.selectTitle')"
      @select="onModelSelect"
    />

    <!-- ==================== 正文文档链接操作 ==================== -->
    <van-action-sheet
      v-model="showLinkSheet"
      :actions="linkActions"
      :cancel-text="$t('nav.cancel')"
      :description="linkSheetTitle"
      @select="onLinkSheetSelect"
    />
  </div>
</template>

<script>
import knowledgeBaseLogic from './knowledgeBaseLogic';
import PdfViewer from './PdfViewer.vue';

export default {
  name: 'KnowledgeBase',
  components: { PdfViewer },
  mixins: [knowledgeBaseLogic],
  data() {
    return {
      inputFocused: false,
    };
  },
};
</script>

<style scoped>
/* ============================================================
   TEK 知识库 AI 助手 · 商务风格样式（支持浅色 / 深色主题）
   设计令牌见 src/assets/styles/tokens.css
   ============================================================ */

/* ---------- 页面容器 ---------- */
.kb-page {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--app-bg);
  overflow: hidden;
  font-family: var(--font-sans);
  color: var(--ink-800);
  transition: background-color 0.3s ease;
}

/* ---------- 顶部导航（毛玻璃 + 品牌渐变描边） ---------- */
.nav-bar {
  flex-shrink: 0;
  position: relative;
  background: var(--surface-nav);
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  border-bottom: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-sm);
}
.nav-bar::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--grad-brand);
}
.nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 16px;
  max-width: 880px;
  margin: 0 auto;
}
.nav-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.nav-logo {
  display: flex;
  align-items: center;
}
.nav-logo img {
  height: 30px;
}
.nav-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--ink-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nav-right {
  display: flex;
  align-items: center;
  gap: 6px;
}
.nav-icon-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-card);
  color: var(--ink-600);
  border-radius: var(--r-pill);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}
.nav-icon-btn svg {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}
.locale-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav-icon-btn:hover {
  background: var(--brand-50);
  border-color: var(--brand-200);
  color: var(--brand-600);
}
.nav-icon-btn:active {
  transform: scale(0.97);
}
.new-session-btn {
  flex-shrink: 0;
  padding: 5px 12px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-card);
  color: var(--ink-600);
  border-radius: var(--r-pill);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}
.new-session-btn:hover {
  border-color: var(--brand-400);
  color: var(--brand-600);
}
.new-session-btn:active {
  transform: scale(0.97);
}

/* ---------- 主面板 ---------- */
.main-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ---------- 消息滚动区 ---------- */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 18px 16px 10px;
  scroll-behavior: smooth;
}

/* ---------- 欢迎区（含品牌光晕记忆点） ---------- */
.welcome-area {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 44px;
  text-align: center;
}
.welcome-area::before {
  content: "";
  position: absolute;
  top: 8px;
  left: 50%;
  width: 220px;
  height: 220px;
  transform: translateX(-50%);
  background: radial-gradient(circle at 50% 50%, rgba(74, 120, 240, 0.22), rgba(74, 120, 240, 0) 70%);
  filter: blur(8px);
  z-index: 0;
  pointer-events: none;
  animation: orbPulse 4s ease-in-out infinite;
}
.welcome-icon {
  position: relative;
  z-index: 1;
  margin-bottom: 14px;
  animation: welcomeFloat 3.4s ease-in-out infinite;
}
@keyframes orbPulse {
  0%, 100% { opacity: 0.7; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.08); }
}
@keyframes welcomeFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
.welcome-title {
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ink-900);
}
.welcome-desc {
  position: relative;
  z-index: 1;
  margin: 0 0 26px;
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.7;
  max-width: 300px;
}

/* ---------- 常见问题 ---------- */
.faq-section {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 880px;
  padding: 0 0 44px;
}
.faq-header {
  margin-bottom: 16px;
  text-align: left;
}
.faq-header-title {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: var(--ink-800);
  margin-bottom: 4px;
}
.faq-header-desc {
  display: block;
  font-size: 12px;
  color: var(--text-muted);
}
.faq-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
@media (min-width: 540px) {
  .faq-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}
.faq-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  animation: cardFadeIn 0.45s ease forwards;
  opacity: 0;
}
.faq-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--grad-brand);
  transform: scaleY(0);
  transform-origin: top;
  transition: transform 0.25s ease;
}
@keyframes cardFadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.faq-card:hover {
  border-color: var(--brand-200);
  box-shadow: var(--shadow-lg);
  transform: translateY(-3px);
}
.faq-card:hover::before {
  transform: scaleY(1);
}
.faq-card:active {
  transform: translateY(-1px) scale(0.99);
  background: var(--brand-50);
}
.faq-category {
  display: inline-block;
  align-self: flex-start;
  font-size: 11px;
  font-weight: 600;
  color: var(--brand-700);
  background: var(--brand-50);
  padding: 3px 10px;
  border-radius: var(--r-pill);
}
.faq-question {
  font-size: 14px;
  color: var(--ink-800);
  line-height: 1.55;
}

/* ---------- 消息行 ---------- */
.msg-row {
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
  gap: 10px;
}
.msg-row.user {
  justify-content: flex-end;
}
.msg-row.ai {
  justify-content: flex-start;
}
.msg-avatar {
  flex-shrink: 0;
  margin-top: 2px;
}
.msg-bubble {
  max-width: 78%;
  padding: 13px 17px;
  border-radius: var(--r-lg);
  word-break: break-word;
  line-height: 1.7;
  font-size: 14px;
  animation: bubbleIn 0.3s ease;
}
@keyframes bubbleIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.msg-row.user .msg-bubble {
  background: var(--grad-brand);
  color: #fff;
  border-bottom-right-radius: 6px;
  box-shadow: var(--shadow-brand);
}
.msg-row.ai .msg-bubble {
  background: var(--surface-card);
  color: var(--ink-800);
  border: 1px solid var(--border-subtle);
  border-bottom-left-radius: 6px;
  box-shadow: var(--shadow-sm);
}
.msg-text {
  white-space: pre-wrap;
}
/* ---------- 流式输出文本（打字机）：纯文本展示，done 后再切 Markdown ---------- */
.msg-text.ai-streaming {
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 20px;
}
.stream-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: var(--brand-500);
  border-radius: 1px;
  animation: cursorBlink 1s step-end infinite;
}
@keyframes cursorBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
/* 停止生成按钮（流式输出中） */
.send-btn--stop {
  background: var(--ink-500);
  box-shadow: none;
}
/* ---------- AI 富文本（markdown-it 渲染；v-html 内容需 ::v-deep 命中） ---------- */
.msg-text.ai-md {
  white-space: normal;
  word-break: break-word;
}
.msg-text.ai-md ::v-deep p {
  margin: 0.5em 0;
}
.msg-text.ai-md ::v-deep p:first-child {
  margin-top: 0;
}
.msg-text.ai-md ::v-deep p:last-child {
  margin-bottom: 0;
}
.msg-text.ai-md ::v-deep h1,
.msg-text.ai-md ::v-deep h2,
.msg-text.ai-md ::v-deep h3,
.msg-text.ai-md ::v-deep h4 {
  margin: 0.9em 0 0.45em;
  font-weight: 700;
  line-height: 1.35;
  color: var(--ink-900);
}
.msg-text.ai-md ::v-deep h1 {
  font-size: 18px;
}
.msg-text.ai-md ::v-deep h2 {
  font-size: 16px;
}
.msg-text.ai-md ::v-deep h3 {
  font-size: 15px;
}
.msg-text.ai-md ::v-deep h4 {
  font-size: 14px;
}
.msg-text.ai-md ::v-deep ul,
.msg-text.ai-md ::v-deep ol {
  margin: 0.5em 0;
  padding-left: 1.5em;
}
.msg-text.ai-md ::v-deep li {
  margin: 0.25em 0;
}
.msg-text.ai-md ::v-deep strong {
  font-weight: 700;
  color: var(--ink-900);
}
.msg-text.ai-md ::v-deep em {
  font-style: italic;
}
.msg-text.ai-md ::v-deep blockquote {
  margin: 0.6em 0;
  padding: 6px 12px;
  border-left: 3px solid var(--brand-400);
  background: var(--brand-50);
  color: var(--ink-700);
  border-radius: 0 var(--r-sm) var(--r-sm) 0;
}
.msg-text.ai-md ::v-deep code {
  padding: 1px 5px;
  font-size: 12.5px;
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: 4px;
  font-family: Consolas, "Courier New", monospace;
}
.msg-text.ai-md ::v-deep pre {
  margin: 0.6em 0;
  padding: 10px 12px;
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: var(--r-sm);
  overflow-x: auto;
}
.msg-text.ai-md ::v-deep pre code {
  padding: 0;
  border: none;
  background: none;
}
.msg-text.ai-md ::v-deep hr {
  margin: 1em 0;
  border: none;
  border-top: 1px solid var(--border-subtle);
}
.msg-text.ai-md ::v-deep a {
  color: var(--brand-600);
  text-decoration: underline;
  text-underline-offset: 2px;
}
/* 正文中的文档链接：点击由前端拦截弹操作面板（预览 / 复制下载链接），不直接下载 */
.msg-text.ai-md ::v-deep a.md-file-link {
  cursor: pointer;
}
.msg-text.ai-md ::v-deep a.md-file-link:active {
  opacity: 0.7;
}
.msg-text.ai-md ::v-deep table {
  width: 100%;
  margin: 0.6em 0;
  border-collapse: collapse;
  font-size: 12.5px;
}
.msg-text.ai-md ::v-deep th,
.msg-text.ai-md ::v-deep td {
  padding: 6px 10px;
  border: 1px solid var(--border-subtle);
  text-align: left;
}
.msg-text.ai-md ::v-deep th {
  background: var(--surface-subtle);
  font-weight: 600;
}
/* AI 引用上标（可点击，点击事件经容器委托） */
.msg-text.ai-md ::v-deep .ai-qa-ref {
  display: inline-block;
  min-width: 15px;
  height: 15px;
  line-height: 15px;
  padding: 0 3px;
  margin: 0 1px;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  text-align: center;
  color: var(--brand-600);
  background: var(--brand-50);
  border: 1px solid var(--brand-200);
  border-radius: 4px;
  vertical-align: super;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.msg-text.ai-md ::v-deep .ai-qa-ref:active {
  background: var(--brand-100);
  border-color: var(--brand-400);
}

/* ---------- 加载动画 ---------- */
.loading-bubble {
  padding: 16px 22px;
  min-width: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-dots {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 4px 0;
}
.loading-dots .dot {
  display: block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--brand-500);
  animation: dotBounce 1.2s ease-in-out infinite both;
}
.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes dotBounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

/* ---------- 内容来源 ---------- */
.source-wrap {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--border-subtle);
}
.source-title-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 8px;
}
.source-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: var(--r-sm);
  margin-bottom: 6px;
  font-size: 12px;
  transition: border-color 0.2s ease, background 0.2s ease;
}
.source-item:hover {
  border-color: var(--brand-200);
  background: var(--brand-50);
}
.source-clickable {
  cursor: pointer;
  color: var(--brand-700);
}
.source-copy-btn {
  flex-shrink: 0;
  margin-left: 2px;
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  transition: background 0.15s ease;
}
.source-copy-btn:hover {
  background: rgba(36, 83, 214, 0.1);
}
.source-copy-btn:active {
  background: rgba(36, 83, 214, 0.2);
}
.source-item:last-child {
  margin-bottom: 0;
}
.source-type-badge {
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  color: var(--brand-600);
  background: var(--brand-50);
  border: 1px solid var(--brand-100);
}
.source-name {
  flex: 1;
  color: var(--ink-800);
}

/* ---------- 输入栏（毛玻璃） ---------- */
.input-bar {
  flex-shrink: 0;
  background: var(--surface-input);
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  border-top: 1px solid var(--border-subtle);
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}
.input-inner {
  max-width: 880px;
  margin: 0 auto;
}
.input-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.input-field {
  flex: 1;
  height: 54px;
  padding: 0 22px;
  border: 1.5px solid var(--brand-200);
  border-radius: var(--r-pill);
  background: var(--surface-card);
  font-size: 14px;
  color: var(--ink-800);
  outline: none;
  box-shadow: 0 2px 10px rgba(36, 83, 214, 0.10);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}
.input-field::placeholder {
  color: var(--text-muted);
}
.input-field:focus {
  border-color: var(--brand-500);
  box-shadow: 0 0 0 4px rgba(56, 102, 238, 0.12);
}
.input-field:disabled {
  background: var(--surface-subtle);
  color: var(--ink-300);
}
.send-btn {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--grad-brand);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  box-shadow: var(--shadow-brand);
}
.send-btn:hover:not(:disabled) {
  transform: scale(1.06);
  box-shadow: 0 12px 28px rgba(36, 83, 214, 0.38);
  filter: brightness(1.04);
}
.send-btn:active:not(:disabled) {
  transform: scale(0.95);
}
.send-btn:disabled {
  background: var(--brand-200);
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.85;
}

/* ---------- 首次引导输入（常见问题上方，强化视觉吸引） ---------- */
.hero-input {
  width: 100%;
  max-width: 880px;
  margin: 0 auto 22px;
}
.input-bar--hero {
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-top: none;
  padding: 0;
}
.input-row--hero {
  gap: 12px;
}
.hero-field {
  flex: 1;
  display: flex;
  padding: 3px;
  border-radius: var(--r-pill);
  background: var(--grad-brand);
  box-shadow: 0 10px 30px rgba(36, 83, 214, 0.20);
  animation: heroPulse 2.4s ease-in-out infinite;
  position: relative;
}
.hero-field .input-field {
  height: 56px;
  border: none;
  border-radius: var(--r-pill);
  background: var(--surface-card);
  font-size: 15px;
  padding: 0 24px;
  box-shadow: none;
}
.hero-field .input-field:focus {
  border: none;
  box-shadow: none;
}
.hero-field .input-field::placeholder {
  color: var(--text-muted);
}
.send-btn--hero {
  width: 54px;
  height: 54px;
}
.hero-hint {
  margin: 12px 0 0;
  text-align: center;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--text-muted);
}
@keyframes heroPulse {
  0%, 100% { box-shadow: 0 10px 30px rgba(36, 83, 214, 0.20); }
  50% { box-shadow: 0 12px 40px rgba(36, 83, 214, 0.42); }
}

/* ---------- AI 生成内容提示（输入框下方，居中微型提示） ---------- */
.ai-disclaimer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 7px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--text-muted);
  white-space: nowrap;
}
.ai-disclaimer svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  opacity: 0.7;
}

/* ---------- 对话后常见问题迷你条（横向滚动 + 左右箭头） ---------- */
.faq-mini {
  position: relative;
  max-width: 880px;
  margin: 6px auto 0;
}
.faq-mini-scroll {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 0 2px;
}
.faq-mini-scroll::-webkit-scrollbar {
  display: none;
}
.faq-mini-chip {
  flex-shrink: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 5px 10px;
  font-size: 11.5px;
  line-height: 1.3;
  color: var(--ink-700);
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.faq-mini-chip:hover {
  border-color: var(--brand-200);
  color: var(--brand-700);
}
.faq-mini-chip:active {
  background: var(--brand-50);
  border-color: var(--brand-200);
  color: var(--brand-700);
}
/* 左右滚动箭头按钮 */
.faq-mini-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--border-subtle);
  background: var(--surface-card);
  color: var(--ink-600);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease;
}
.faq-mini-arrow:hover {
  border-color: var(--brand-200);
  color: var(--brand-600);
}
.faq-mini-arrow:active {
  transform: translateY(-50%) scale(0.92);
}
.faq-mini-arrow:focus-visible {
  outline: 2px solid var(--brand-500);
  outline-offset: 1px;
}
.faq-mini-arrow--left {
  left: -5px;
}
.faq-mini-arrow--right {
  right: -5px;
}

/* ---------- 底部输入增强：左侧提问图标，提升可见性 ---------- */
.input-field-wrap {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}
.input-leading-icon {
  position: absolute;
  left: 16px;
  color: var(--brand-500);
  pointer-events: none;
}
.input-field-wrap .input-field {
  padding-left: 46px;
}

/* ---------- 文件查看器 ---------- */
.file-viewer {
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #0b1020;
}
.viewer-header {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  padding-top: calc(12px + env(safe-area-inset-top));
  gap: 12px;
  flex-shrink: 0;
  background: rgba(11, 16, 32, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.viewer-title {
  flex: 1;
  color: #fff;
  font-size: 15px;
  text-align: center;
}
.viewer-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}
.full-media {
  width: 100%;
  max-height: 100%;
}
.video-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.video-error-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  text-align: center;
}
.full-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
.audio-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 40px 20px;
}
.audio-name {
  color: #fff;
  font-size: 15px;
  margin: 0;
  text-align: center;
}
.audio-ctrl {
  width: 100%;
  max-width: 320px;
}
.no-support {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  text-align: center;
}
.no-support-title {
  color: #fff;
  font-size: 15px;
  margin: 0;
}
.no-support-desc {
  color: var(--text-muted);
  font-size: 13px;
  margin: 0 0 6px;
}
.dl-btn {
  padding: 8px 18px;
  border: 1px solid var(--brand-400);
  background: transparent;
  color: #8fb0ff;
  border-radius: var(--r-sm);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.dl-btn:hover {
  background: rgba(91, 133, 247, 0.12);
  color: #fff;
}
/* ---------- 可访问性：可见焦点态 ---------- */
.nav-icon-btn:focus-visible,
.new-session-btn:focus-visible,
.faq-card:focus-visible,
.send-btn:focus-visible,
.input-field:focus-visible {
  outline: 2px solid var(--brand-500);
  outline-offset: 2px;
}

/* ---------- 宽屏适配 ≥1024px ---------- */
@media (min-width: 1024px) {
  .nav-inner {
    padding: 0 28px;
    height: 60px;
  }
  .nav-title {
    font-size: 19px;
  }
  .chat-messages {
    padding: 28px 24px 14px;
  }
  .msg-row,
  .welcome-area {
    max-width: 880px;
    margin-left: auto;
    margin-right: auto;
  }
  .msg-bubble {
    max-width: 70%;
    padding: 15px 20px;
    border-radius: 18px;
    font-size: 15px;
    line-height: 1.75;
  }
  .msg-row.user .msg-bubble {
    border-bottom-right-radius: 8px;
  }
  .msg-row.ai .msg-bubble {
    border-bottom-left-radius: 8px;
  }
  .welcome-area {
    padding-top: 72px;
  }
  .welcome-title {
    font-size: 28px;
  }
  .welcome-desc {
    font-size: 15px;
    max-width: 380px;
    margin-bottom: 38px;
  }
  .faq-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  .faq-card {
    padding: 20px 22px;
    border-radius: 18px;
  }
  .faq-section {
    margin-top: 6px;
  }
  .faq-header {
    margin-bottom: 22px;
  }
  .input-bar {
    padding: 14px 28px;
    padding-bottom: 14px;
  }
  .input-field {
    height: 56px;
    border-radius: var(--r-pill);
    padding: 0 26px;
    font-size: 15px;
  }
  .input-field-wrap .input-field {
    padding-left: 46px;
  }
  .input-field:focus {
    box-shadow: 0 0 0 4px rgba(56, 102, 238, 0.1);
  }
  .send-btn {
    width: 56px;
    height: 56px;
  }
}
</style>

<!-- 模型选择 ActionSheet / 引用弹层全局样式（van-popup 挂载在 body，需非 scoped） -->
<style>
.action-group-title {
  font-size: 12px !important;
  color: #64748b !important;
  padding: 10px 16px 4px !important;
}
.action-group-title .van-action-sheet__name {
  line-height: 1.4 !important;
}
.action-item-selected {
  color: #2453d6 !important;
  font-weight: 600;
}

/* ---------- 引用片段弹层 ---------- */
.ref-popup {
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  background: var(--surface-card);
}
.ref-popup-body {
  max-width: 640px;
  margin: 0 auto;
  padding: 18px 18px calc(18px + env(safe-area-inset-bottom));
  background: var(--surface-card);
  color: var(--ink-800);
}
.ref-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.ref-popup-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink-900);
}
.ref-popup-close {
  color: var(--text-muted);
  padding: 4px;
  cursor: pointer;
}
.ref-popup-content {
  max-height: 45vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.ref-chunk-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-700);
  line-height: 1.5;
}
.ref-chunk-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: var(--ink-800);
  white-space: pre-wrap;
  word-break: break-word;
}
.ref-popup-footer {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}
.ref-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 18px;
  border: none;
  border-radius: var(--r-pill);
  background: var(--grad-brand);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--shadow-brand);
}
</style>
